import { filter, includes } from "lodash";

import {
  EXTERNAL_CONTENT_TYPE_LINK,
  LINK_LOCATIONS,
} from "src/consts/externalLinks";

/**
 * Normalizes locations from either the old format (string) or new format (array)
 * @param {string|string[]|undefined} location - Single location string (legacy)
 * @param {string[]|undefined} locations - Array of locations (new format)
 * @param {string[]} defaultLocations - Default locations if neither is provided
 * @returns {string[]} Array of normalized locations
 */
export const normalizeLocations = (
  location,
  locations,
  defaultLocations = [LINK_LOCATIONS.APPS]
) => {
  if (Array.isArray(locations) && locations.length > 0) {
    return locations;
  }
  if (location) {
    return [location];
  }
  return defaultLocations;
};

/**
 * Checks if a link should be visible to a user based on their role
 * @param {string[]|undefined} linkRoles - Roles allowed to see the link
 * @param {string} userRole - Current user's role
 * @returns {boolean} True if link should be visible
 */
export const isLinkVisibleToRole = (linkRoles, userRole) => {
  // If no roles specified, show to all (backward compatibility)
  if (!linkRoles || linkRoles.length === 0) {
    return true;
  }
  // API returns lowercase roles; useUserRole returns uppercase - compare case-insensitively
  const normalizedUserRole = userRole?.toLowerCase?.();
  const normalizedLinkRoles = linkRoles.map((r) => r?.toLowerCase?.());
  return includes(normalizedLinkRoles, normalizedUserRole);
};

/**
 * Checks if a link should be visible to a user based on their roles (array)
 * @param {string[]|undefined} linkRoles - Roles allowed to see the link
 * @param {string[]} userRoles - Current user's roles
 * @returns {boolean} True if link should be visible
 */
const isLinkVisibleToUserRoles = (linkRoles, userRoles) => {
  if (!linkRoles || linkRoles.length === 0) {
    return true;
  }
  const normalizedLinkRoles = linkRoles.map((r) => r?.toLowerCase?.());
  return userRoles.some((role) =>
    includes(normalizedLinkRoles, role?.toLowerCase?.())
  );
};

/**
 * Filters external contents by location and user roles (array)
 * @param {Object} params
 * @param {Array} params.externalContents - Array of external content items
 * @param {string|string[]} params.targetLocations - Location(s) to filter for
 * @param {string[]} params.userRoles - Current user's roles
 * @returns {Array} Filtered array of links
 */
export const filterLinksByLocationAndUserRoles = ({
  externalContents,
  targetLocations,
  userRoles = [],
}) => {
  if (!externalContents) {
    return [];
  }

  const targetLocationsArray = Array.isArray(targetLocations)
    ? targetLocations
    : [targetLocations];

  return filter(externalContents, (content) => {
    if (content.type !== EXTERNAL_CONTENT_TYPE_LINK) {
      return false;
    }

    const locations = normalizeLocations(content.location, content.locations, [
      LINK_LOCATIONS.APPS,
    ]);

    const shouldShow = targetLocationsArray.some((targetLoc) =>
      includes(locations, targetLoc)
    );

    if (!shouldShow) {
      return false;
    }

    return isLinkVisibleToUserRoles(content.roles, userRoles);
  });
};

/**
 * Filters external contents by location and user role
 * @param {Array} externalContents - Array of external content items
 * @param {string|string[]} targetLocations - Location(s) to filter for
 * @param {string} userRole - Current user's role
 * @returns {Array} Filtered array of links
 */
export const filterLinksByLocationAndRole = (
  externalContents,
  targetLocations,
  userRole
) => {
  if (!externalContents) {
    return [];
  }

  const targetLocationsArray = Array.isArray(targetLocations)
    ? targetLocations
    : [targetLocations];

  return filter(externalContents, (content) => {
    if (content.type !== EXTERNAL_CONTENT_TYPE_LINK) {
      return false;
    }

    const locations = normalizeLocations(content.location, content.locations, [
      LINK_LOCATIONS.APPS,
    ]);

    const shouldShow = targetLocationsArray.some((targetLoc) =>
      includes(locations, targetLoc)
    );

    if (!shouldShow) {
      return false;
    }

    return isLinkVisibleToRole(content.roles, userRole);
  });
};
